import { FinancialAccountType } from "../types/financialAccount";
import { BankStatementTransaction } from "../types/financialDocument";
import { isLiabilityAccount } from "./financialAccount";
import {
    monetaryValuesEqual,
    subtractMonetaryValues,
    sumMonetaryValues,
} from "./monetary";

export interface ValidateTransactionsAgainstBalancesParams {
    transactions: BankStatementTransaction[];
    openingBalance: number | null;
    closingBalance: number | null;
    accountType: FinancialAccountType | null;
}

export interface ValidateTransactionsAgainstBalancesResult {
    amountsMatch: boolean;
    signsDeterminable: boolean;
    shouldFlipSigns: boolean;
    discrepancyAmount: number | null;
}

export function validateTransactionsAgainstBalances({
    transactions,
    openingBalance,
    closingBalance,
    accountType,
}: ValidateTransactionsAgainstBalancesParams): ValidateTransactionsAgainstBalancesResult {
    if (openingBalance === null || closingBalance === null) {
        return {
            amountsMatch: false,
            signsDeterminable: false,
            shouldFlipSigns: false,
            discrepancyAmount: null,
        };
    }
    const transactionSum = sumMonetaryValues(
        transactions.map((t: BankStatementTransaction) => t.amount),
    );
    const changeInBalance = subtractMonetaryValues(
        closingBalance,
        openingBalance,
    );
    const isLiability = accountType !== null && isLiabilityAccount(accountType);
    const signsAreOpposite =
        (transactionSum > 0 && changeInBalance < 0) ||
        (transactionSum < 0 && changeInBalance > 0);
    const amountsMatch = monetaryValuesEqual(
        Math.abs(transactionSum),
        Math.abs(changeInBalance),
    );
    const signsDeterminable =
        (amountsMatch && changeInBalance !== 0) || transactions.length === 0;
    const shouldFlipSigns =
        signsDeterminable &&
        transactions.length !== 0 &&
        accountType !== null &&
        (isLiability ? !signsAreOpposite : signsAreOpposite);
    const effectiveSum = isLiability ? -transactionSum : transactionSum;
    const discrepancyAmount = subtractMonetaryValues(
        effectiveSum,
        changeInBalance,
    );
    return {
        amountsMatch,
        signsDeterminable,
        shouldFlipSigns,
        discrepancyAmount,
    };
}
